import "@/plugins/awVisibleData";
import "@/plugins/dayjsAdapterForChartjs";
import { navigationNamespace } from "@/store/modules/navigation";
import Chart, { ChartDataSets } from "chart.js";
import "chartjs-plugin-colorschemes";
import {
  Classic10,
  ClassicLight10
} from "chartjs-plugin-colorschemes/src/colorschemes/colorschemes.tableau";
import _ from "lodash";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { CHART_TOOLTIP_FORMAT } from "../util";

export function defaultTooltipLabelCallback(
  tooltipItem: Chart.ChartTooltipItem,
  data: Chart.ChartData
) {
  const dataSets = data.datasets as ChartDataSets[];
  const index = tooltipItem.datasetIndex as number;
  let label = dataSets[index].label || "";

  if (label) {
    label += ": ";
  }
  label += Math.round((tooltipItem.yLabel as number) * 100) / 100;
  return label;
}

const MAX_VISIBLE_DOTS_LARGE = 40;
const MAX_VISIBLE_DOTS_SMALL = 25;
const LARGE_SCREEN_MIN_WIDTH = 500;

@Component({})
export default class VueChart extends Vue {
  @Prop({
    required: true,
    default: () => {
      return [];
    }
  })
  data!: any;

  @Prop(Object)
  library!: any;

  @Prop(String)
  type!: string;

  @Prop(Number)
  height!: number;

  @Prop(Number)
  min!: number;

  @Prop(Number)
  max!: number;

  @Prop(Number)
  suggestedMin!: number;

  @Prop(Number)
  suggestedMax!: number;

  @Prop(Boolean)
  initialized!: boolean;

  @Getter("darkMode", navigationNamespace)
  darkMode!: boolean;

  chart!: Chart;
  ctx!: CanvasRenderingContext2D;
  chartRef!: HTMLCanvasElement;
  defaultConfig!: Chart.ChartConfiguration;

  get scaleFontSize() {
    const height = this.height;
    if (height) {
      if (height > 280) {
        return 20;
      } else if (height <= 280 && height > 200) {
        return 18;
      } else if (height <= 200 && height > 150) {
        return 16;
      } else {
        return 14;
      }
    } else {
      return 14;
    }
  }

  mounted() {
    this.renderChart();
  }

  renderChart() {
    if (!this.initialized || !this.height) {
      return;
    }

    this.cleanUp();

    this.chartRef = this.$refs.chart as HTMLCanvasElement;
    const ctx = this.chartRef.getContext("2d");
    if (!ctx) {
      return;
    }
    this.ctx = ctx;

    const yTicks: Record<string, number> = {};

    if (this.max === 0 || this.max) {
      yTicks.max = this.max;
    }
    const hasSuggestedMin = this.suggestedMin === 0 || this.suggestedMin;
    if (this.min === 0 || this.min) {
      yTicks.min = this.min;
    } else if (!hasSuggestedMin) {
      // Default min to 0 unless min/suggested min has been provided
      yTicks.min = 0;
    }

    if (hasSuggestedMin) {
      yTicks.suggestedMin = this.suggestedMin;
    }

    if (this.suggestedMax === 0 || this.suggestedMax) {
      yTicks.suggestedMax = this.suggestedMax;
    }

    yTicks.maxTicksLimit = 5;
    yTicks.fontSize = this.scaleFontSize;

    const scheme = this.darkMode ? ClassicLight10 : Classic10;
    if (this.darkMode) {
      Chart.defaults.global.defaultFontColor = "#DCDCDC";
      Chart.defaults.scale.gridLines.color = "#DCDCDC";
    } else {
      Chart.defaults.global.defaultFontColor = "#666";
      Chart.defaults.scale.gridLines.color = "#666";
    }
    const width = (this.$refs.canvasContainer as HTMLElement).offsetWidth;
    const largeScreen = width > LARGE_SCREEN_MIN_WIDTH;
    const maxDots = largeScreen
      ? MAX_VISIBLE_DOTS_LARGE
      : MAX_VISIBLE_DOTS_SMALL;
    const datasets = this.data.map((dataset: any) => {
      dataset.pointHitRadius = 5;
      dataset.pointHoverRadius = dataset.pointRadius
        ? dataset.pointRadius + 2
        : 6;
      if (dataset.data.length > maxDots) {
        if (!dataset.previousPoint) {
          dataset.previousPoint = {
            pointRadius: dataset.pointRadius,
            pointHoverRadius: dataset.pointHoverRadius
          };
        }
        // remove point style and show line when too few dots
        if (dataset.pointStyle && dataset.showLine === false) {
          dataset.previousPoint.pointStyle = dataset.pointStyle;
          dataset.previousPoint.showLine = dataset.showLine;

          delete dataset.pointStyle;
          dataset.showLine = true;
        }

        // show no points when too few dots
        dataset.pointRadius = 0;
        dataset.pointHoverRadius = 3;
      } else {
        // revert point size
        if (dataset.previousPoint) {
          dataset.pointRadius = dataset.previousPoint.pointRadius;
          dataset.pointHoverRadius = dataset.previousPoint.pointHoverRadius;
          dataset.pointStyle = dataset.previousPoint.pointStyle;
          dataset.showLine = dataset.previousPoint.showLine;
          delete dataset.previousPoint;
        }

        if (!largeScreen && dataset.showLine === false) {
          // when not showing a line, decrease the point size for smaller screens
          dataset.pointRadius = 4;
          dataset.pointHoverRadius = 5;
        }
      }
      return dataset;
    });

    this.defaultConfig = {
      type: this.type || "line",
      data: { datasets },
      options: {
        plugins: {
          colorschemes: {
            scheme
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          callbacks: {
            label: defaultTooltipLabelCallback
          }
        },
        scales: {
          yAxes: [
            {
              ticks: yTicks
            }
          ],
          xAxes: [
            {
              type: "time",
              time: {
                tooltipFormat: CHART_TOOLTIP_FORMAT
              },
              ticks: {
                maxTicksLimit: 4,
                maxRotation: 0,
                fontSize: this.scaleFontSize
              }
            }
          ]
        }
      }
    };

    const config = _.merge({}, this.defaultConfig, {
      options: this.library
    });

    // Apply yTick change after merge because there might
    // be multiple axes and _.merge will only update the ones in
    // the yAxes[0] spot
    config.options.scales.yAxes.forEach((axes: any) => {
      _.merge(axes.ticks, yTicks, axes.ticks);
    });

    this.chart = new Chart(this.ctx, config);
    // To make it fill the space
    this.chart.update();
  }

  @Watch("library")
  onLibraryChange() {
    if (!this.chart || !this.chart.options) {
      return;
    }
    _.merge(this.chart.options, this.library);
    this.chart.update();
  }

  // Re-render chart when range changes
  @Watch("min")
  @Watch("max")
  @Watch("data")
  @Watch("initialized")
  @Watch("$vuetify.breakpoint.name")
  @Watch("height")
  onDataChange() {
    this.renderChart();
  }

  beforeDestroy() {
    this.cleanUp();
  }

  cleanUp() {
    if (this.chart) {
      this.chart.destroy();
    }
  }
}
